import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, ChevronDown, Clock, Facebook, Headset, Instagram, Linkedin, Mail, MapPin, Phone, Youtube } from "lucide-react";
import { Logo } from "../Logo";
import { CookieConsentChip } from "@/components/consent/CookieConsentChip";
import { Button } from "@/components/ui/button";
import { OAKLAND_CONTACT, SAN_JOSE_CONTACT } from "@/data/site";
import { trackPublicCta } from "@/lib/track";

type Col = { title: string; links: { label: string; href: string }[] };

const COLUMNS: Col[] = [
  {
    title: "Our Story",
    links: [
      { label: "About CWS", href: "/about#story" },
      { label: "Vietnam Operations", href: "/about#vietnam" },
      { label: "Leadership", href: "/leadership" },
      { label: "Governance & Compliance", href: "/governance" },
      { label: "Sustainability", href: "/sustainability" },
      { label: "News", href: "/news" },
      { label: "Press Releases", href: "/news/press-releases" },
      { label: "Newsroom & Press Room", href: "/newsroom" },
      { label: "35 Years", href: "/35-years" },
      { label: "Careers", href: "/careers" },
    ],
  },
  {
    title: "Services",
    links: [
      { label: "Oakland Recycling", href: "/oakland" },
      { label: "San José Recycling", href: "/san-jose" },
      { label: "Commercial", href: "/commercial" },
      { label: "Recycling Guide", href: "/recycling-guide" },
    ],
  },
  {
    title: "Support",
    links: [
      { label: "Oakland Support", href: "/oakland#oakland-customer-support" },
      { label: "San José Support", href: "/san-jose#san-jose-customer-support" },
      { label: "Report a Missed Pickup", href: "/report-a-missed-pickup" },
      { label: "Commercial Quote", href: "/commercial#quote" },
      { label: "Pay Commercial Invoice", href: "/pay-commercial-invoice" },
      { label: "Billing & Account Help", href: "/billing" },
      { label: "Sales / Media / Partnerships", href: "/contact#other" },
      { label: "FAQs", href: "/contact#faqs" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Guides & Downloads", href: "/resources" },
      { label: "What Goes In", href: "/recycling-guide#in" },
      { label: "What Stays Out", href: "/recycling-guide#out" },
      { label: "FAQs", href: "/faqs" },
      { label: "Service Updates", href: "/news" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Use", href: "/legal/terms" },
      { label: "Cookie Policy", href: "/legal/cookies" },
    ],
  },
];

// Links carry min-h-6 + py-1 so every hit area clears WCAG 2.2 AA (2.5.8, 24x24px).
// List spacing is reduced to keep the rendered rhythm close to the previous design.
const LinkList = forwardRef<HTMLUListElement, { links: Col["links"] }>(({ links }, ref) => (
  <ul ref={ref} className="space-y-0.5">
    {links.map((l) => (
      <li key={l.label}>
        <Link
          to={l.href}
          className="inline-flex min-h-6 items-center py-1 text-[13.5px] leading-snug text-white/75 transition-colors hover:text-white"
        >
          {l.label}
        </Link>
      </li>
    ))}
  </ul>
));
LinkList.displayName = "LinkList";

export const Footer = () => {
  return (
    <footer className="relative overflow-hidden bg-[hsl(var(--primary-deep))] text-white">
      {/* Brand rule */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-blue-green" />
      

      {/* Top band — logo + description + key CTAs */}
      <div className="relative border-b border-white/10">
        <div className="container-cws py-10">
          <div className="grid gap-8 lg:grid-cols-12 lg:items-center">
            <div className="flex items-start gap-5 lg:col-span-7">
               <div className="rounded-md bg-white/95 p-2 shadow-lg ring-1 ring-white/30">
                <Logo kind="square" size="lg" />
              </div>
              <div>
                <p className="font-display text-[16px] font-bold leading-tight text-white">
                  California Waste Solutions
                </p>
                <p className="mt-1.5 max-w-xl text-[13.5px] leading-relaxed text-white/75 text-pretty">
                  California Waste Solutions provides recycling, resource recovery, customer support, and environmental infrastructure services for communities, businesses, and public partners.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2.5 lg:col-span-5 lg:justify-end">
              <Button asChild size="default" className="whitespace-nowrap bg-white text-primary hover:bg-white/90" onClick={() => trackPublicCta("Oakland Support", { target_path: "/oakland#oakland-customer-support", city: "Oakland" })}>
                <Link to="/oakland#oakland-customer-support" id="cta-footer-oakland-support" data-gtm="cta_click" data-gtm-id="oakland_support" data-gtm-location="footer" data-gtm-type="cta"><Headset className="h-4 w-4" /> Oakland Support</Link>
              </Button>
              <Button asChild variant="green" size="default" className="whitespace-nowrap" onClick={() => trackPublicCta("San José Support", { target_path: "/san-jose#san-jose-customer-support", city: "San José" })}>
                <Link to="/san-jose#san-jose-customer-support" id="cta-footer-sanjose-support" data-gtm="cta_click" data-gtm-id="san_jose_support" data-gtm-location="footer" data-gtm-type="cta"><Headset className="h-4 w-4" /> San José Support</Link>
              </Button>
              <Button asChild size="default" className="whitespace-nowrap border border-white/40 bg-transparent text-white hover:bg-white/10" onClick={() => trackPublicCta("Commercial Quote", { target_path: "/commercial#quote" })}>
                <Link to="/commercial#quote" id="cta-footer-commercial-quote" data-gtm="cta_click" data-gtm-id="commercial_quote" data-gtm-location="footer" data-gtm-type="cta"><Building2 className="h-4 w-4" /> Commercial Quote</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Middle — link columns */}
      <div className="relative container-cws py-12">
        <div className="grid gap-10 lg:grid-cols-12">
          {/* Desktop columns */}
          <div className="hidden lg:col-span-7 lg:grid lg:grid-cols-5 lg:gap-8">
            {COLUMNS.map((col) => (
              <div key={col.title}>
                <h3 className="mb-3 whitespace-nowrap font-display text-[12px] font-bold uppercase tracking-[0.16em] text-white">
                  {col.title}
                </h3>
                <LinkList links={col.links} />
              </div>
            ))}
          </div>

          {/* Mobile / tablet — left-aligned accordion */}
          <div className="lg:hidden divide-y divide-white/10 border-y border-white/10">
            {COLUMNS.map((col) => (
              <details key={col.title} className="group">
                <summary className="flex cursor-pointer list-none items-center gap-4 py-4 text-left font-display text-[15px] font-bold text-white [&::-webkit-details-marker]:hidden [&::marker]:hidden">
                  {/* Plain element: the desktop column list above already exposes these as h3 headings. */}
                  <span className="flex-1 text-left font-display text-[15px] font-bold text-white">{col.title}</span>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white/80 transition-transform duration-300 group-open:rotate-45">
                    <span aria-hidden className="relative block h-3.5 w-3.5">
                      <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-current" />
                      <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-current" />
                    </span>
                  </span>
                </summary>
                <div className="pb-5 pt-1">
                  <LinkList links={col.links} />
                </div>
              </details>
            ))}
          </div>

          {/* Contact block */}
          <div className="lg:col-span-5">
            <h3 className="mb-4 font-display text-[12px] font-bold uppercase tracking-[0.16em] text-white">
              Contact California Waste Solutions
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
               <div className="rounded-md border border-white/15 bg-white/5 p-4 transition-colors hover:border-white/30">
                <p className="font-display text-[13.5px] font-bold text-white">{OAKLAND_CONTACT.name}</p>
                <a href={`tel:${OAKLAND_CONTACT.phone.replace(/[^\d]/g, "")}`} data-gtm="phone_click" data-gtm-id="oakland_phone" data-gtm-location="footer" data-gtm-type="phone" className="mt-2 flex min-h-6 items-center gap-2 whitespace-nowrap py-0.5 text-[13px] text-white/85 hover:text-white">
                  <Phone className="h-3.5 w-3.5 shrink-0 text-accent" />{OAKLAND_CONTACT.phone}
                </a>
                <a href={`mailto:${OAKLAND_CONTACT.email}`} data-gtm="email_click" data-gtm-id="oakland_email" data-gtm-location="footer" data-gtm-type="email" className="mt-1.5 flex min-h-6 items-center gap-2 whitespace-nowrap py-0.5 text-[13px] text-white/70 hover:text-white">
                  <Mail className="h-3.5 w-3.5 shrink-0 text-accent" />{OAKLAND_CONTACT.email}
                </a>
              </div>
               <div className="rounded-md border border-white/15 bg-white/5 p-4 transition-colors hover:border-white/30">
                <p className="font-display text-[13.5px] font-bold text-white">{SAN_JOSE_CONTACT.name}</p>
                <a href={`tel:${SAN_JOSE_CONTACT.phone.replace(/[^\d]/g, "")}`} data-gtm="phone_click" data-gtm-id="san_jose_phone" data-gtm-location="footer" data-gtm-type="phone" className="mt-2 flex min-h-6 items-center gap-2 whitespace-nowrap py-0.5 text-[13px] text-white/85 hover:text-white">
                  <Phone className="h-3.5 w-3.5 shrink-0 text-accent" />{SAN_JOSE_CONTACT.phone}
                </a>
                <a href={`mailto:${SAN_JOSE_CONTACT.email}`} data-gtm="email_click" data-gtm-id="san_jose_email" data-gtm-location="footer" data-gtm-type="email" className="mt-1.5 flex min-h-6 items-center gap-2 whitespace-nowrap py-0.5 text-[13px] text-white/70 hover:text-white">
                  <Mail className="h-3.5 w-3.5 shrink-0 text-accent" />{SAN_JOSE_CONTACT.email}
                </a>
              </div>
               <div className="rounded-md border border-white/15 bg-white/5 p-4 sm:col-span-2">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-display text-[13.5px] font-bold text-white">Headquarters</p>
                    <p className="mt-2 flex items-start gap-2 text-[13px] text-white/75">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
                      <span>1211 Embarcadero, Suite 300<br />Oakland, CA 94606</span>
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="font-display text-[13.5px] font-bold text-white">Support Hours</p>
                    <p className="mt-2 flex items-start gap-2 whitespace-nowrap text-[13px] text-white/75">
                      <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
                      <span>Mon to Fri · 8:00 AM to 6:00 PM</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 flex items-center gap-4 text-white/70">
              {[
                { Icon: Facebook, label: "Facebook", href: "https://www.facebook.com/calwastesolutions/" },
                { Icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/californiawastesolutions" },
                { Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/californiawastesolutions/" },
                { Icon: Youtube, label: "YouTube", href: "https://www.youtube.com/@CaliforniaWasteSolutions" },
              ].map(({ Icon, label, href }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/5 transition-all hover:-translate-y-0.5 hover:border-white/40 hover:bg-white/10 hover:text-white">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
              <Link to="/contact" className="ml-auto inline-flex min-h-6 items-center gap-1 py-1 text-[12.5px] font-semibold text-white hover:text-accent">
                All contact options <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-white/10">
        <div className="container-cws flex flex-col items-start justify-between gap-2 py-4 text-[9px] font-semibold uppercase tracking-[0.12em] text-white/55 sm:flex-row sm:items-center sm:gap-3 sm:text-[10px] sm:tracking-[0.16em]">
          <p className="whitespace-nowrap">© {new Date().getFullYear()} California Waste Solutions · All Rights Reserved</p>
          <p className="whitespace-nowrap">Serving Communities Since <span className="text-white/85">1992</span></p>
        </div>
        <div className="container-cws flex flex-col items-start justify-between gap-3 border-t border-white/5 py-3 sm:flex-row sm:items-center">
          <CookieConsentChip />
          <nav
            aria-label="Legal"
            className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/55"
            title="By using this site, you agree to our Terms and Cookie Policy"
          >
            <Link to="/legal/terms" className="inline-flex min-h-6 items-center py-1 transition-colors hover:text-white">Terms</Link>
            <span aria-hidden className="text-white/25">·</span>
            <Link to="/legal/cookies" className="inline-flex min-h-6 items-center py-1 transition-colors hover:text-white">Cookie Policy</Link>
          </nav>
        </div>
      </div>

    </footer>
  );
};
